'use client';

import { useEffect, useState } from 'react';
import { Link2 as LinkIcon } from 'lucide-react';

import type { PostLinkPreview } from '@kit/community';
import { cdnImageUrl } from '@kit/shared/storage';

// ---------------------------------------------------------------------------
// Link attachment
// ---------------------------------------------------------------------------

// One in-flight/settled unfurl per URL per page load — cold links call
// /api/link-preview once (which caches server-side for every future
// viewer); duplicates of the same URL across cards share the promise.
const previewWarmups = new Map<string, Promise<PostLinkPreview | null>>();

function warmLinkPreview(href: string): Promise<PostLinkPreview | null> {
  const existing = previewWarmups.get(href);
  if (existing) return existing;
  const promise = fetch(`/api/link-preview?url=${encodeURIComponent(href)}`)
    .then((res) => (res.ok ? res.json() : { preview: null }))
    .then((body: { preview?: PostLinkPreview | null }) => body.preview ?? null)
    .catch(() => null);
  previewWarmups.set(href, promise);
  return promise;
}

/**
 * Link attachment. Renders the rich unfurl card (Threads/X-style: OG image
 * on top, domain kicker, title, clamped description) when preview metadata
 * exists; falls back to the compact hostname card otherwise. Cold links
 * (no server-attached preview) warm the shared cache via /api/link-preview
 * and upgrade in place when metadata arrives.
 */
export function LinkAttachment({
  href,
  preview: initialPreview,
}: {
  href: string;
  preview?: PostLinkPreview | null;
}) {
  const [preview, setPreview] = useState<PostLinkPreview | null>(
    initialPreview ?? null,
  );
  // External og:image loaded raw (no proxy); hotlink-blocked / expired / 404
  // origins would otherwise render a broken-image box. On load failure we
  // record the failed URL and drop the image (falling back to the text-only
  // rich card, or the compact card). Keying on the URL rather than a boolean
  // means a later preview with a different image self-heals with no effect.
  const [erroredImageUrl, setErroredImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (initialPreview !== undefined && initialPreview !== null) return;
    let cancelled = false;
    void warmLinkPreview(href).then((fetched) => {
      if (!cancelled && fetched) setPreview(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, [href, initialPreview]);

  let hostname = href;
  let path = '';

  try {
    const url = new URL(href);
    hostname = url.hostname.replace(/^www\./, '');
    path = `${url.pathname}${url.search}`.replace(/^\/$/, '');
  } catch {
    // keep raw href as hostname
  }

  const showImage =
    Boolean(preview?.imageUrl) && preview?.imageUrl !== erroredImageUrl;

  if (preview && (preview.title || showImage)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="border-border hover:bg-muted/50 group block overflow-hidden rounded-lg border transition-colors"
      >
        {showImage && (
          <div className="bg-muted aspect-[1.91/1] w-full overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cdnImageUrl(preview.imageUrl, 'GALLERY') ?? undefined}
              alt=""
              loading="lazy"
              onError={() => setErroredImageUrl(preview.imageUrl)}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </div>
        )}
        <div className="flex flex-col gap-0.5 p-3">
          <span className="text-muted-foreground truncate text-xs">
            {preview.siteName ?? hostname}
          </span>
          {preview.title && (
            <span className="text-foreground line-clamp-2 text-sm font-medium group-hover:underline">
              {preview.title}
            </span>
          )}
          {preview.description && (
            <span className="text-muted-foreground line-clamp-2 text-xs">
              {preview.description}
            </span>
          )}
        </div>
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="border-border hover:bg-muted/50 group flex items-center gap-3 rounded-lg border p-3 transition-colors"
    >
      <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
        <LinkIcon
          className="text-muted-foreground h-4 w-4"
          aria-hidden="true"
        />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="text-foreground truncate text-sm font-medium group-hover:underline">
          {hostname}
        </span>
        {path && (
          <span className="text-muted-foreground truncate text-xs">{path}</span>
        )}
      </div>
    </a>
  );
}
